import React, { useCallback } from 'react';
import { useDataset } from '../../hooks/useDataset.js';
import { useSelector } from 'react-redux';
import { useBaseWidget } from '../../hooks/useBaseWidget.jsx';
import { useChartWidget } from '../../hooks/useChartWidget.js';
import ChartContent from '../base/ChartContent.jsx';
import { arraysHaveSameElements } from '../../DashboardGridIndex.jsx';

function SimplePieDatasetWidget({ widgetId, widgetRef }) {
    const { numberWithUnit, formatNumber } = useBaseWidget();
    const { legendTitleFormatter } = useChartWidget();
    const {data, loading, getNumberUnit, getNumberPrecision} = useDataset({widgetId})
    const datasets = useSelector(state => state.dashboard.widgets[widgetId].settings?.datasets) || [];
    const dataset_type = useSelector(state => state.dashboard.widgets[widgetId].settings?.dataset_type) || "single";
    const colorScheme = useSelector(state => state.dashboard.colorScheme, arraysHaveSameElements);
    const legend_text_color = useSelector(state => state.dashboard.widgets[widgetId].settings?.legend_text_color);
    const number_format = useSelector(state => state.dashboard.widgets[widgetId].settings?.number_format);
    const display_labels_on_data = useSelector(state => state.dashboard.widgets[widgetId].settings?.display_labels_on_data);

    const getChartPlugins = useCallback(() => {
        // No configuration option to increase the space between legend and chart
        // https://stackoverflow.com/a/67723827
        const addPaddingBetweenLegendAndChartPlugin = {
            beforeInit(chart) {
                // Get reference to the original fit function
                const originalFit = chart.legend.fit;
            
                // Override the fit function
                chart.legend.fit = function fit() {
                // Call original function and bind scope in order to use `this` correctly inside it
                originalFit.bind(chart.legend)();
                // Change the height
                this.height += 25;
                }
            }
        }

        // To avoid overlapping
        // https://stackoverflow.com/questions/45352194/chart-js-v2-6-add-arrows-to-pie-chart-output-values/72731158#72731158
        const getSuitableY = (y, yArray = [], direction) => {
            let result = y;
            yArray.forEach((existedY) => {
                if (existedY - 14 < result && existedY + 14 > result) {
                if (direction === "right") {
                    result = existedY + 14;
                } else {
                    result = existedY - 14;
                }
                }
            });
            return result;
        };

        const getOriginPoints = (source, center, l) => {
            let a = {x: 0, y: 0};
            var dx = (center.x - source.x) / l
            var dy = (center.y - source.y) / l
            a.x = center.x + l * dx
            a.y = center.y + l * dy
            return a
        };

        const labelPlugin = {
            afterDraw: (chart) => {
                const ctx = chart.ctx;
                ctx.save();
                ctx.font = "10px";
                const leftLabelCoordinates = [];
                const rightLabelCoordinates = [];
                const chartCenterPoint = {
                x:
                    (chart.chartArea.right - chart.chartArea.left) / 2 +
                    chart.chartArea.left,
                y:
                    (chart.chartArea.bottom - chart.chartArea.top) / 2 +
                    chart.chartArea.top
                };
                chart.config.data.labels.forEach((label, i) => {
                const meta = chart.getDatasetMeta(0);
                const arc = meta.data[i];
                const dataset = chart.config.data.datasets[0];
                if(arc.circumference ==0){
                    return
                }
                // Prepare data to draw
                // important point 1
                const centerPoint = arc.getCenterPoint();
                let color = chart.config._config.data.datasets[0].backgroundColor[i];
                let labelColor = chart.config._config.data.datasets[0].backgroundColor[i];


                const angle = Math.atan2(
                    centerPoint.y - chartCenterPoint.y,
                    centerPoint.x - chartCenterPoint.x
                );
                // important point 2, this point overlapsed with existed points
                // so we will reduce y by 14 if it's on the right
                // or add by 14 if it's on the left
                let originPoint = getOriginPoints(chartCenterPoint, centerPoint, arc.outerRadius)
                const point2X =
                    chartCenterPoint.x + Math.cos(angle) * (centerPoint.x < chartCenterPoint.x ? arc.outerRadius + 10 : arc.outerRadius + 10);
                let point2Y =
                    chartCenterPoint.y + Math.sin(angle) * (centerPoint.y < chartCenterPoint.y ? arc.outerRadius + 15 : arc.outerRadius + 15);

                let suitableY;
                if (point2X < chartCenterPoint.x) {
                    // on the left
                    suitableY = getSuitableY(point2Y, leftLabelCoordinates, "left");
                } else {
                    // on the right

                    suitableY = getSuitableY(point2Y, rightLabelCoordinates, "right");
                }

                point2Y = suitableY;

                let value = formatNumber(dataset.data[i], getNumberUnit(i), getNumberPrecision(i), number_format);
                // if (dataset.polyline && dataset.polyline.formatter) {
                //   value = dataset.polyline.formatter(value);
                // }
                let edgePointX = point2X < chartCenterPoint.x ? chartCenterPoint.x - arc.outerRadius - 10 : chartCenterPoint.x + arc.outerRadius + 10;

                if (point2X < chartCenterPoint.x) {
                    leftLabelCoordinates.push(point2Y);
                } else {
                    rightLabelCoordinates.push(point2Y);
                }

                //DRAW CODE
                // first line: connect between arc's center point and outside point
                ctx.lineWidth = 2;
                ctx.strokeStyle = color;
                ctx.beginPath();
                ctx.moveTo(originPoint.x, originPoint.y);
                ctx.lineTo(point2X, point2Y);
                ctx.stroke();
                // second line: connect between outside point and chart's edge
                ctx.beginPath();
                ctx.moveTo(point2X, point2Y);
                ctx.lineTo(edgePointX < chartCenterPoint.x ? edgePointX+3 : edgePointX-3 , point2Y);
                ctx.stroke();
                //fill custom label
                const labelAlignStyle =
                    edgePointX < chartCenterPoint.x ? "right" : "left";
                const labelX = edgePointX < chartCenterPoint.x ? edgePointX : edgePointX + 0;
                const labelY = point2Y + 7;
                ctx.textAlign = labelAlignStyle;
                ctx.textBaseline = "bottom";
                ctx.font = "12px";
                ctx.fillStyle = labelColor;
                ctx.fillText(value, labelX, labelY);
                });
                ctx.restore();
            }
        }
        let with_labels = display_labels_on_data == "with_labels";
        return with_labels ? [addPaddingBetweenLegendAndChartPlugin, labelPlugin] : [];
  }, [display_labels_on_data])

    const getChartOptions = useCallback(() => {
        return {
        maintainAspectRatio: false,
        devicePixelRatio: 2,
        layout:{
            padding: {
            bottom: 30,
            left: 50,
            right: 50
            }
        },
        plugins: {
            datalabels: {
            anchor: 'end',
            align: 'end',
            offset: 5,
            // rotation: 45,
            // display: 'auto',
            labels: {
                value: {
                color: legend_text_color
                }
            }
            },
            tooltip: {
            callbacks: {
                label: (tooltipItem) => formatNumber(tooltipItem.parsed, getNumberUnit(tooltipItem.dataIndex), getNumberPrecision(tooltipItem.dataIndex), number_format),
            },
            },
            legend: {
            labels: {
                color: legend_text_color,
                generateLabels: (chart) => {
                let data = chart.data;

                if (Array.isArray(data.datasets) && data.datasets.length > 1)
                    return data.datasets.map((dataset, i)=> {
                    return {
                        text: legendTitleFormatter(dataset.label, widgetId),
                        fillStyle: (!Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor : dataset.backgroundColor[0]),
                        hidden: !chart.isDatasetVisible(i),
                        strokeStyle: dataset.borderColor,
                        pointStyle: dataset.pointStyle,
                        datasetIndex: i
                        };
                    })
                else if(Array.isArray(data.labels) && data.datasets.length > 0) {
                    let visibility = [];
                    for(let i = 0; i < data.labels.length; i++) {
                    visibility.push(chart.getDataVisibility(i) === true ? false : true)
                    }
                    return data.labels.map((label, i) => {
                    return {
                        text: legendTitleFormatter(label, widgetId),
                        strokeStyle: (data.datasets[0].borderColor || {})[i],
                        fillStyle: (data.datasets[0].backgroundColor || {})[i],
                        hidden: visibility[i],
                        datasetIndex: i
                    };
                    })
                } else [];
                }
            },
            },
        }
        };
    }, [legend_text_color, number_format])

    const formatData = useCallback(() => {
        let labels = [];
        let values = [];
        let colors = [];
        let notConfigured = datasets.length == 0;

        // Datasets does not always have same labels, get a list of unique labels and its color
        const colorSet = {}
        if(data){
        const labelList = []
        Object.values(data).forEach((d)=>{
            d.map((l)=>{
            if(!labelList.includes(l.label)){
                labelList.push(l.label)
            }
            })
        })
        labelList.sort((a, b) => a && b && a.toString().localeCompare(b.toString())).forEach((d,i)=>{
            colorSet[d] = colorScheme[i % colorScheme.length]
        })
        }

        datasets.forEach((d, i) => {
            // If any of the variables is missing dataset_id, report is not configured.
            notConfigured = notConfigured || !(d.dataset_id || d.value);
            const value = (((data || {})[d.dataset_id] || {})[0] || {}).data;

            if (dataset_type == "grouped"){
                ((data || {})[d.dataset_id] || []).sort((a, b) => a.label && b.label && a.label.toString().localeCompare(b.label.toString())).forEach((ds, j) => {
                const idx = labels.findIndex((d) => d.label == ds.label);
                let data = ds.data;
                const colorWithScheme = colorScheme[j % colorScheme.length]
                if (idx == -1){
                    labels.push(ds.label || d.name_item);
                    values.push(numberWithUnit(data))
                    colors.push(colorSet ? colorSet[ds.label] : (colorWithScheme ? colorWithScheme : d.background_color))
                } else {
                    data += values[idx]
                    values[idx] = numberWithUnit(data)
                }
                });

            } else {
                labels.push(d.name_item);
                values.push(numberWithUnit(value === void 0 ? d.value : value, d.number_unit));
                colors.push(d.background_color);
            }
        });

        return {
            notConfigured,
            labels,
            datasets: [
                {
                data: values,
                backgroundColor: colors,
                },
            ],
        };
    }, [dataset_type, colorScheme, datasets, data]);

    return (
        <ChartContent chart_type='pie' {...{formatData, getChartOptions, getChartPlugins, data, widgetId, widgetRef}} />
    )
}

export default SimplePieDatasetWidget;
